/**
 * ActivationFunnelService — emit the activation-funnel steps this app was not
 * already recording.
 *
 * Uses the mechanism Voice already has: the `logger` edge function, which is
 * how suite visits are recorded for ordinary (non-admin) customers. It does
 * NOT go through `ApiService.invoke({ resource: "activities", action: "create" })`
 * — that handler returns FORBIDDEN unless the caller is an admin or the service
 * role, which is precisely the population whose funnel we need to measure. No
 * new analytics vendor, no new client library.
 *
 * Rules:
 *   - Fire and forget. Instrumentation never blocks, slows or breaks a customer
 *     flow. Every failure is swallowed.
 *   - Emit at the moment the thing genuinely happened, not on render.
 *   - Duplicates are harmless: every metric takes the EARLIEST event per
 *     account, so a repeated emit cannot move a number.
 *
 * The one backend piece this needs — `case "funnel"` in supabase/functions/
 * logger/index.ts, a direct mirror of suite.ts — is specified in
 * docs/handoff/time-to-value-instrumentation.md. Until it lands these calls
 * are no-ops that cost one failed request and change nothing the customer sees.
 */
import { LoggerService } from "@/services/logger/LoggerService";
import { UserService } from "@/services/users/UserService";
import type {
  ActivationFunnelEvent,
  PackSharedMethod,
} from "@/types/analytics/activation-funnel";

/** The minimum of an activation pack row needed to resolve its Genie. */
interface PackGenieSource {
  agent_id?: string | null;
  agent?: { id?: string | null } | null;
}

export class ActivationFunnelService {
  /**
   * Record one funnel step. Never throws, never returns an error to the caller
   * — a broken metric must never become a broken product.
   */
  static async record(event: ActivationFunnelEvent): Promise<void> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();

      if (!user?.id) return;

      await LoggerService.logFunnelStep({
        userId: user.id,
        step: event.step,
        genieId: event.genieId ?? null,
        channel: event.channel ?? null,
        method: event.method ?? null,
        detail: event.detail ?? null,
        occurredAt: new Date().toISOString(),
      });
    } catch (error) {
      // Deliberately quiet in production. A funnel event is not worth a toast,
      // a retry, or a line in the customer's console.
      if (import.meta.env.DEV) {
        console.warn("[activation-funnel] could not record step", event.step, error);
      }
    }
  }

  /** Convenience: a channel became able to reach the Genie. */
  static channelConnected(
    channel: ActivationFunnelEvent["channel"],
    method: ActivationFunnelEvent["method"],
    genieId?: string | null,
  ): void {
    void ActivationFunnelService.record({ step: "channel_connected", channel, method, genieId });
  }

  /**
   * Convenience: an activation pack now exists.
   *
   * `genieId` is null for a portal-scoped pack, which has no Genie. The event
   * still counts as a pack being raised, so it is emitted with a null
   * resource id rather than dropped.
   */
  static activationPackGenerated(genieId: string | null): void {
    void ActivationFunnelService.record({ step: "activation_pack_generated", genieId });
  }

  /** Convenience: a forwarded number was proven to reach the Genie. */
  static forwardingVerified(genieId: string, carrierId: string, mode: string): void {
    void ActivationFunnelService.record({
      step: "forwarding_verified",
      channel: "phone",
      method: "forwarded",
      genieId,
      detail: { carrier_id: carrierId, mode },
    });
  }

  /**
   * Convenience: the owner took the pack out into the world.
   *
   * `how` describes the MECHANISM by which the pack left the app, not the
   * owner's intention:
   *   - `download` — a file left the browser (one asset, the whole zip, or the
   *     print specification sheet).
   *   - `print`    — a print order was actually sent to a vendor.
   *   - `link`     — a shareable pack URL was copied to the clipboard.
   *   - `email`    — a pack link was emailed to someone.
   *
   * Keeping this to the mechanism is what makes the Operations console able to
   * group on it. "Downloaded a print spec sheet" is a download, not a print;
   * only a sent order is a print.
   */
  static packShared(
    genieId: string | null,
    how: PackSharedMethod,
    detail?: Record<string, string | number | boolean | null>,
  ): void {
    void ActivationFunnelService.record({
      step: "pack_shared",
      genieId,
      detail: { ...detail, how },
    });
  }

  /**
   * The Genie an activation pack points at, or null for a portal-scoped pack.
   *
   * One place, because every `pack_shared` call site needs it and the pack row
   * carries the Genie id either flat (`agent_id`) or on the joined target.
   */
  static packGenieId(pack: PackGenieSource | null | undefined): string | null {
    return pack?.agent_id ?? pack?.agent?.id ?? null;
  }
}
